import React, { useState, useEffect, useCallback } from 'react';
import { getStoreIdFromToken, apiRequest } from '../../utils/api';
import TableQrManager from './tableQrManager';
import BusinessHoursField from './businessHoursField';
import PrinterManager from './printerManager';
import MenuOutputSettings from './menuOutputSettings';
import SalesPage from './salesPage';
import backIcon from '../../assets/icons/common/back_icon.png';
import adminStoreinfoIcon from '../../assets/icons/admin/storeinfo.png';
import bottomNavBg from '../../assets/icons/common/bottomNav.png';

const StoreSettings = ({ isOpen, onClose, onSaveSuccess, onSyncStart, initialStoreData, menuDisplayMode = 'pos', isModeSwitching = false, onMenuDisplayModeChange, businessOpenTime = '', businessCloseTime = '', onBusinessHoursChange }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // props에서 초기값 설정
  const [storeData, setStoreData] = useState({
    storeName: initialStoreData?.storeName || '',
    businessName: initialStoreData?.businessName || '',
    address: initialStoreData?.address || '',
    phoneNumber: initialStoreData?.phoneNumber || '',
    businessNumber: initialStoreData?.businessNumber || '',
    origins: initialStoreData?.origins || ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [posType, setPosType] = useState(initialStoreData?.pos_type || '');
  const [isDataLoaded, setIsDataLoaded] = useState(
    !!(initialStoreData && initialStoreData.pos_type !== undefined)
  );
  const [isTableQrManagerOpen, setIsTableQrManagerOpen] = useState(false);
  const [isPrinterManagerOpen, setIsPrinterManagerOpen] = useState(false);
  const isPrinterManagerOpenRef = React.useRef(false);
  const [isMenuOutputOpen, setIsMenuOutputOpen] = useState(false);
  const isMenuOutputOpenRef = React.useRef(false);
  const [isSalesPageOpen, setIsSalesPageOpen] = useState(false);
  const isSalesPageOpenRef = React.useRef(false);
  const [notifySound, setNotifySound] = useState(true);
  const [notifyPopup, setNotifyPopup] = useState(true);
  // 마지막으로 저장된 알림설정 — 값이 실제로 바뀐 경우에만 PUT(→ 클라이언트 broadcast) 하기 위한 기준값
  const lastNotifyRef = React.useRef({ sound: true, popup: true });
  const isTableQrManagerOpenRef = React.useRef(false);
  const isScannerOpenRef = React.useRef(false);
  const closeScannerRef = React.useRef(null);

  // Refs for input fields
  const storeNameRef = React.useRef(null);
  const businessNameRef = React.useRef(null);
  const addressRef = React.useRef(null);
  const phoneNumberRef = React.useRef(null);
  const businessNumberRef = React.useRef(null);
  const originsRef = React.useRef(null);

  // 자동 저장용 refs
  const saveTimeoutRef = React.useRef(null);
  const persistRef = React.useRef(null); // 최신 persistChanges 보관 (closure 회피)
  // popstate listener는 mount 시 1회만 등록되도록 handleClose도 ref로 우회 (parent re-render churn 무관)
  const handleCloseRef = React.useRef(null);

  // Define handleClose function early to avoid reference errors
  const handleClose = useCallback(() => {
    // 보류된 debounce 타이머 취소 + 닫기 직전 즉시 저장 (포커스 중 뒤로가기 케이스)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (persistRef.current) {
      // fire-and-forget: 사용자에겐 즉시 닫기 경험, 저장은 백그라운드 처리
      persistRef.current();
    }

    // 좀비 history 정리는 popstate useEffect cleanup에서 수행 (handleClose에서 직접 history.back 하면
    // popstate 경로로 들어왔을 때 이중 pop이 발생해 부모 화면까지 빠지는 버그 발생)
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  }, [onClose]);

  // handleClose 변경 시 ref 갱신 — popstate listener는 ref를 참조하므로 churn 안 받음
  React.useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  // props 로 storeData 초기화 — 패널이 열려있는 동안 "최초 1회"만.
  // (자동저장 성공 → 부모 storeInfo 갱신 → initialStoreData 새 참조 → 재초기화 → 재저장…
  //  무한 루프로 매장정보/알림설정 PUT 이 800ms마다 계속 나가던 문제 차단. 패널은 조건부
  //  마운트라 닫으면 언마운트되어 ref 가 리셋되므로, 다시 열면 새로 초기화됨.)
  const storeDataInitedRef = React.useRef(false);
  useEffect(() => {
    if (isOpen && initialStoreData && initialStoreData.pos_type !== undefined && !storeDataInitedRef.current) {
      storeDataInitedRef.current = true;
      setStoreData({
        storeName: initialStoreData.storeName || '',
        businessName: initialStoreData.businessName || '',
        address: initialStoreData.address || '',
        phoneNumber: initialStoreData.phoneNumber || '',
        businessNumber: initialStoreData.businessNumber || '',
        origins: initialStoreData.origins || ''
      });
      setPosType(initialStoreData.pos_type || '');
      setIsDataLoaded(true);
    }
  }, [isOpen, initialStoreData]);

  // 알림 설정 조회
  useEffect(() => {
    if (!isOpen) return;

    const fetchNotificationSettings = async () => {
      try {
        const storeId = await getStoreIdFromToken();
        if (!storeId) return;
        const data = await apiRequest(`/store-settings/${storeId}/notification-settings`);
        if (data.success) {
          setNotifySound(data.enableNotifySound);
          setNotifyPopup(data.enableNotifyPopup);
          // 조회한 값을 기준값으로 저장 → 패널 열 때 자동저장이 알림 PUT 을 재전송하지 않도록
          lastNotifyRef.current = { sound: data.enableNotifySound, popup: data.enableNotifyPopup };
        }
      } catch (err) {
        console.error('Failed to fetch notification settings:', err);
      }
    };

    fetchNotificationSettings();
  }, [isOpen, posType]);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Start animation after a brief delay
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // pushState - isOpen 변경 시에만 실행 (history entry 1번만 추가)
  useEffect(() => {
    if (!isOpen) return;

    window.history.pushState({ storeSettings: true }, '', window.location.href);
  }, [isOpen]);

  // 뒤로가기 처리 (Android + 웹 브라우저) - pushState와 분리
  // handleClose는 ref로 참조 → parent re-render churn에도 listener 재등록 안 됨
  // 자식 모달(scanner / tableQrManager)이 열려있을 땐 그것만 닫고 storeSettings는 유지
  // 재pushState는 하지 않음 — popstate 시 이미 자기 항목이 pop되므로 추가 push하면 이중 pop 유발
  useEffect(() => {
    if (!isOpen) return;

    const handleBack = () => {
      // 스캐너가 열려있으면 스캐너만 닫기
      if (isScannerOpenRef.current) {
        if (closeScannerRef.current) {
          closeScannerRef.current();
        }
        return;
      }
      // tableQrManager가 열려있으면 tableQrManager만 닫기
      if (isTableQrManagerOpenRef.current) {
        setIsTableQrManagerOpen(false);
        isTableQrManagerOpenRef.current = false;
        return;
      }
      // PrinterManager 가 열려있으면 PrinterManager 가 자체 popstate listener 로 close —
      // storeSettings 는 이 popstate 무시 (안 그러면 storeSettings 도 같이 닫힘)
      if (isPrinterManagerOpenRef.current) {
        return;
      }
      // 상품별 출력 설정도 자체 popstate 로 close — 동일하게 무시
      if (isMenuOutputOpenRef.current) {
        return;
      }
      // 매출 페이지도 자체 popstate 로 close — 동일하게 무시
      if (isSalesPageOpenRef.current) {
        return;
      }
      // 죽은 child modal entry 의 pop 일 수 있음 — 자기 entry 가 아직 살아있으면 (state.storeSettings)
      // 아직 storeSettings 차례 아님 → 무시. (예: tableQr X 버튼으로 닫혀 stack 에 죽은 entry 남은 케이스)
      if (window.history.state?.storeSettings) {
        return;
      }
      // storeSettings 닫기 — ref로 최신 handleClose 호출
      handleCloseRef.current?.();
    };

    window.handleAndroidBack = handleBack;
    const handlePopState = () => { handleBack(); };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.handleAndroidBack = null;
      window.removeEventListener('popstate', handlePopState);
      // 우리 push한 history 항목이 아직 top이면 정리 (UI 닫기 경로의 좀비 방지)
      if (window.history.state?.storeSettings) {
        window.history.back();
      }
    };
  }, [isOpen]);  // ← handleClose 의존 제거: listener는 모달 열림 동안 1회만 등록

  // 화면 크기에 따른 스케일 조정
  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.store-settings-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        const scale = Math.min(viewportWidth / 360, 1.5);
        wrapper.style.zoom = scale;
      }
    };

    if (isVisible) {
      scaleUI();
    }
  }, [isVisible]);

  // Detect keyboard visibility on mobile
  useEffect(() => {
    const handleResize = () => {
      // On mobile, viewport height changes when keyboard appears
      const windowHeight = window.innerHeight;
      
      // If window height is significantly smaller than screen height, keyboard is likely visible
      if (window.visualViewport) {
        const hasKeyboard = window.visualViewport.height < window.screen.height * 0.75;
        setIsKeyboardVisible(hasKeyboard);
      } else {
        // Fallback for browsers without visualViewport
        const hasKeyboard = windowHeight < window.screen.height * 0.75;
        setIsKeyboardVisible(hasKeyboard);
      }
    };

    if (isVisible) {
      // Listen for viewport changes
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
      } else {
        window.addEventListener('resize', handleResize);
      }
      
      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize);
        } else {
          window.removeEventListener('resize', handleResize);
        }
      };
    }
  }, [isVisible]);


  // Prevent body scroll when store settings is open
  useEffect(() => {
    let scrollY = 0;
    
    if (isOpen) {
      // 현재 스크롤 위치 저장
      scrollY = window.scrollY;
      
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      // cleanup 시 스크롤 위치 복원
      const bodyTop = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // top 값에서 스크롤 위치 추출하여 복원
      if (bodyTop) {
        const restoredScrollY = parseInt(bodyTop, 10) * -1;
        window.scrollTo(0, restoredScrollY);
      }
    };
  }, [isOpen]);

  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '');
    
    // 길이가 너무 짧으면 그대로 반환
    if (numbers.length <= 3) {
      return numbers;
    }
    
    // 서울 지역번호 (02)
    if (numbers.startsWith('02')) {
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 5) return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
      if (numbers.length <= 9) return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
      if (numbers.length === 10) return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
    }
    
    // 지역번호 (031, 032, 033, 041, 042, 043, 044, 051, 052, 053, 054, 055, 061, 062, 063, 064)
    const areaCode = numbers.slice(0, 3);
    const areaCodes = ['031', '032', '033', '041', '042', '043', '044', '051', '052', '053', '054', '055', '061', '062', '063', '064'];
    
    // 휴대폰 번호 (010, 011, 016, 017, 018, 019)
    const mobilePrefixes = ['010', '011', '016', '017', '018', '019'];
    
    // 특수번호 (070, 080 등)
    const specialPrefixes = ['070', '080', '050', '060'];
    
    // 대표번호 (15XX, 16XX, 18XX 등)
    if (numbers.startsWith('15') || numbers.startsWith('16') || numbers.startsWith('18')) {
      if (numbers.length <= 4) return numbers;
      if (numbers.length <= 8) return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 8)}`;
    }
    
    // 휴대폰 번호 처리
    if (mobilePrefixes.includes(areaCode)) {
      if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
      if (numbers.length === 10) return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
      if (numbers.length === 11) return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
    
    // 지역번호 처리
    if (areaCodes.includes(areaCode) || specialPrefixes.includes(areaCode)) {
      if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
      if (numbers.length <= 10) return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
      if (numbers.length === 11) return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
    
    // 기본 포맷팅 (알 수 없는 형식)
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    } else if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  // 사업자 번호 포맷팅 함수
  const formatBusinessNumber = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '');
    
    // 사업자 번호 포맷팅 (XXX-XX-XXXXX)
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 5) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 10)}`;
    }
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;
    
    // 전화번호 필드인 경우 포맷팅
    if (field === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value);
    }
    // 사업자 번호 필드인 경우 포맷팅
    else if (field === 'businessNumber') {
      formattedValue = formatBusinessNumber(value);
    }
    
    setStoreData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
    // 에러 상태 초기화
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: false
      }));
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const handleNotificationToggle = (field, value) => {
    if (field === 'sound') setNotifySound(value);
    if (field === 'popup') setNotifyPopup(value);
  };

  // 자동 저장 — 매장 정보 + 알림 설정을 silent로 백엔드에 PUT
  // 저장 버튼이 없으므로 매번 토스트 띄우지 않고, 부모에는 silent=true 전달
  const persistChanges = useCallback(async () => {
    // 매장명 비어있으면 자동 저장 skip (사용자가 의도적으로 비웠을 수 있고, 백엔드 거부 가능)
    if (!storeData.storeName || storeData.storeName.trim() === '') return;

    try {
      const storeId = await getStoreIdFromToken();
      if (!storeId) return;

      const failed = [];

      try {
        const response = await apiRequest(`/store-by-id/${storeId}/business-info`, {
          method: 'PUT',
          body: JSON.stringify({
            store_name: storeData.storeName,
            ingredient_info: storeData.origins,
            business_name: storeData.businessName,
            business_address: storeData.address,
            business_phone: storeData.phoneNumber,
            business_number: storeData.businessNumber
          })
        });
        if (!response.success) failed.push('매장 정보');
      } catch {
        failed.push('매장 정보');
      }

      // 알림설정은 값이 실제로 바뀐 경우에만 PUT — 백엔드가 이 PUT 시에만 okdc/union 클라이언트로
      // 신호를 broadcast 하므로, 매장정보 편집/패널 열기·닫기마다 재전송되던 문제 방지.
      if (notifySound !== lastNotifyRef.current.sound || notifyPopup !== lastNotifyRef.current.popup) {
        try {
          const notifyResponse = await apiRequest(`/store-settings/${storeId}/notification-settings`, {
            method: 'PUT',
            body: JSON.stringify({
              enableNotifySound: notifySound,
              enableNotifyPopup: notifyPopup
            })
          });
          if (!notifyResponse.success) failed.push('알림 설정');
          else lastNotifyRef.current = { sound: notifySound, popup: notifyPopup };
        } catch {
          failed.push('알림 설정');
        }
      }

      if (failed.length > 0) {
        if (onSaveSuccess) onSaveSuccess({ error: true, errorItems: failed });
        return;
      }

      // 자동 저장 성공: silent로 부모 상태만 동기화 (토스트 X)
      if (onSaveSuccess) {
        onSaveSuccess({
          silent: true,
          storeName: storeData.storeName,
          origins: storeData.origins,
          businessName: storeData.businessName,
          address: storeData.address,
          phoneNumber: storeData.phoneNumber,
          businessNumber: storeData.businessNumber
        });
      }
    } catch (error) {
      console.error('Failed to auto-save store settings:', error);
      if (onSaveSuccess) onSaveSuccess({ error: true });
    }
  }, [storeData, notifySound, notifyPopup, onSaveSuccess]);

  // handleClose에서 closure 없이 최신 persistChanges 호출하도록 ref에 보관
  React.useEffect(() => {
    persistRef.current = persistChanges;
  }, [persistChanges]);

  // 변경 발생 시 debounce 800ms 후 자동 저장
  React.useEffect(() => {
    if (!isDataLoaded) return; // 초기 로딩 중에는 저장 trigger 안 함
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      persistChanges();
      saveTimeoutRef.current = null;
    }, 800);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [storeData, notifySound, notifyPopup, isDataLoaded, persistChanges]);

  if (!isVisible) return null;

  return (
    <>
      <style jsx="true">{`
        .store-settings {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 10000;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .store-settings.open {
          transform: translateX(0);
        }

        .store-settings-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          overflow: hidden;
        }

        .store-header {
          width: 100%;
          height: 55px;
          background-color: #ffffff;
          border-bottom: 1px solid #E8E8E8;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          flex-shrink: 0;
        }

        .back-button {
          position: absolute;
          left: 18px;
          width: 27px;
          height: 27px;
          cursor: pointer;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .header-icon {
          width: 26px;
          height: 26px;
        }

        .header-title {
          font-size: 18px;
          font-weight: bold;
          color: #000000;
        }

        .store-settings-content {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 0;
        }
        
        /* Custom Scrollbar */
        .store-settings-content::-webkit-scrollbar {
          width: 6px;
        }
        
        .store-settings-content::-webkit-scrollbar-track {
          background: transparent;
          margin-top: 0; /* 헤더 바로 아래부터 시작 */
          margin-bottom: 0; /* 저장 버튼 제거 — 끝까지 */
        }
        
        .store-settings-content::-webkit-scrollbar-thumb {
          background: rgb(201, 201, 201);
          border-radius: 3px;
          min-height: 50px; /* 최소 높이 설정 */
        }
        
        .store-settings-content::-webkit-scrollbar-thumb:hover {
          background: rgb(170, 170, 170);
        }

        .store-content {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 48px;
          padding-top: 36px;
          padding-bottom: 0;
        }

        .section {
          padding: 0 22px;
        }

        /* 버튼 3개(상품별출력·프린터·QR)는 store-content 의 48px gap 대신 좁게 묶는다 */
        .button-stack {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .logout-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 100px;         /* 마지막 섹션 아래 100px 간격 */
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 8px;              /* 10px → 8px */
        }

        .logout-icon {
          width: 18px;               /* 20px → 18px */
          height: 18px;
        }

        .logout-button span {
          font-size: 13px;           /* 14px → 13px */
          font-weight: 600;
          color: #ABABAB;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 9px;
          margin-bottom: 20px;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #000000;
        }

        .section-subtitle {
          font-size: 12px;
          font-weight: 400;
          color: #ABABAB;
        }

        .input-container {
          background-color: #ffffff;
          border: 1px solid #E8E8E8;
          border-radius: 8px;
          padding: 10px 14px 10px 14px;
          transition: background-color 0.2s ease, border-color 0.2s ease;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          min-height: 60px;
        }

        .input-container.focused {
          border-color: #FDCD27;
        }

        .input-container.filled:not(.focused) {
          background-color: #FAFAFB;
        }

        .input-container.filled:not(.focused) .input-field {
          background-color: #FAFAFB;
        }

        .input-container.error {
          border-color: #F41717;
          border-width: 1px;
          animation: shake 0.3s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
        
        @keyframes shake {
          0% { transform: translateX(0); }
          12.5% { transform: translateX(-1px); }
          25% { transform: translateX(1px); }
          37.5% { transform: translateX(-1px); }
          50% { transform: translateX(1px); }
          62.5% { transform: translateX(-1px); }
          75% { transform: translateX(1px); }
          87.5% { transform: translateX(-1px); }
          100% { transform: translateX(0); }
        }

        .input-label {
          font-size: 12px;
          font-weight: 500;
          color: #888888;
          margin-bottom: 8px;
          flex-shrink: 0;
        }

        .input-label.error {
          color: #F41717;
        }

        .input-field {
          width: 100%;
          border: none;
          outline: none;
          font-size: 16px;
          font-weight: 500;
          color: #000000;
          resize: none;
          line-height: 19px;
          overflow: hidden;
          min-height: 19px;
          background-color: transparent;
          transition: background-color 0.2s ease;
          caret-color: #FDCD27;
        }
        
        /* When input has value, make it semibold */
        .input-field:not(:placeholder-shown) {
          font-weight: 600;
        }

        .input-field::placeholder {
          color: #BEBEBE;
          font-weight: 500;
        }

        .business-info-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .input-container.readonly {
          background-color: #F5F5F5;
          border-color: #E0E0E0;
        }

        .input-field.readonly {
          color: #666666;
          cursor: not-allowed;
        }

        .fixed-bottom {
          background-image: url(${bottomNavBg});
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center bottom;
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 360px;
          height: 100px;
          display: flex;
          justify-content: center;
          align-items: flex-end;
          padding-bottom: 16px;
          z-index: 1000;
          pointer-events: none;
        }
        
        .store-save-button {
          width: 328px;
          height: 50px;
          background-color: #FDCD27;
          border: none;
          border-radius: 10px;
          font-size: 17px;
          font-weight: 700;
          color: #FFFFFF;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          pointer-events: auto;
        }

        .store-save-button:active {
          transform: scale(0.98);
        }

        .sync-button {
          width: 100%;
          height: 52px;
          background: linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%);
          border: none;
          border-radius: 14px;
          color: white;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.3px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(15, 52, 96, 0.3);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .sync-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.08),
            rgba(255, 255, 255, 0.15),
            rgba(255, 255, 255, 0.08),
            transparent
          );
          animation: syncShimmer 3s ease-in-out infinite;
        }

        @keyframes syncShimmer {
          0% { left: -100%; }
          50% { left: 100%; }
          100% { left: 100%; }
        }

        .sync-button:disabled {
          background: #E0E0E0;
          box-shadow: none;
          cursor: not-allowed;
        }

        .sync-button:disabled::before {
          display: none;
        }

        .sync-button:not(:disabled):active {
          transform: scale(0.97);
          box-shadow: 0 2px 8px rgba(15, 52, 96, 0.2);
        }

        .sync-button-icon {
          width: 16px;
          height: 18px;
          animation: syncIconSpin 4s linear infinite;
        }

        @keyframes syncIconSpin {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(360deg); }
          100% { transform: rotate(360deg); }
        }

        .sync-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .sync-overlay-text {
          font-size: 20px;
          font-weight: 700;
          color: white;
        }

        .sync-overlay-dot {
          display: inline-block;
          animation: wave 1.4s ease-in-out infinite;
        }

        .sync-overlay-dot:nth-child(2) {
          animation-delay: 0.2s;
        }

        .sync-overlay-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes wave {
          0%, 60%, 100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-8px);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @media screen and (min-width: 361px) {
          .store-settings-wrapper {
            zoom: calc(100vw / 360);
          }
        }

        @media screen and (min-width: 540px) {
          .store-settings-wrapper {
            zoom: 1.5;
          }
        }

        @media screen and (max-width: 360px) {
          .store-settings-wrapper {
            width: 360px;
            zoom: calc(100vw / 360);
          }
        }

        /* 로딩 화면 스타일 */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          gap: 16px;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #E8E8E8;
          border-top-color: #FDCD27;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .loading-text {
          font-size: 14px;
          font-weight: 500;
          color: #888888;
        }

        .notification-toggle-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 0;
        }

        .notification-toggle-row + .notification-toggle-row {
          border-top: 1px solid #F0F0F0;
        }

        .notification-toggle-label {
          font-size: 15px;
          font-weight: 500;
          color: #333;
        }

        .notification-toggle-desc {
          font-size: 12px;
          color: #999;
          margin-top: 2px;
        }

        .toggle-switch {
          position: relative;
          width: 48px;
          height: 28px;
          flex-shrink: 0;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #DEE2E6;
          border-radius: 14px;
          transition: background-color 0.2s ease, transform 0.1s ease;
        }

        .toggle-switch:active .toggle-slider {
          transform: scale(0.9);
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          height: 22px;
          width: 22px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          border-radius: 50%;
          transition: transform 0.2s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15);
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: #FDCD27;
        }

        .toggle-switch input:checked + .toggle-slider::before {
          transform: translateX(20px);
        }

        .menu-mode-toggle {
          display: flex;
          gap: 6px;
        }

        .menu-mode-btn {
          flex: 1;
          height: 44px;
          border: 1px solid #E8E8E8;
          border-radius: 12px;
          background-color: #FFFFFF;
          color: #8b8b8b;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .menu-mode-btn.active {
          background-color: #000000;
          border-color: #000000;
          color: #ffffff;
        }

        .menu-mode-btn:disabled {
          opacity: 0.5;
          cursor: default;
        }

        .table-qr-button {
          width: 100%;
          padding: 16px;
          background: #FFFFFF;
          border: 1px solid #E8E8E8;
          border-radius: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.2s ease;
        }

        .table-qr-button:active {
          transform: scale(0.99);
        }

        .table-qr-button:focus {
          outline: none;
          border-color: #E8E8E8;
          background: #FFFFFF;
        }

        .table-qr-button-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .table-qr-button-text {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
        }

        .table-qr-button-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
        }

        .table-qr-button-desc {
          font-size: 12px;
          color: #999;
        }

      `}</style>
      
      <div className={`store-settings ${isAnimating ? 'open' : ''}`}>
        <div className="store-settings-wrapper">
          <div className="store-header">
            <img 
              src={backIcon}
              alt="뒤로가기" 
              className="back-button"
              onClick={handleClose}
            />
            <div className="header-content">
              <img 
                src={adminStoreinfoIcon} 
                alt="매장 설정" 
                className="header-icon"
              />
              <span className="header-title">매장 설정</span>
            </div>
          </div>

          <div
            className="store-settings-content"
          >
            {!isDataLoaded ? (
              <div className="loading-container">
                <div className="loading-spinner" />
                <span className="loading-text">로딩중...</span>
              </div>
            ) : (
              <div className="store-content">
              {/* POS 동기화 버튼 — 매장 타입 무관 단일 버튼.
                  onSyncStart 인자는 매장의 pos_type 그대로 전달 (백엔드 분기용).
                  WowPos 매장은 외부 POS 미연동이라 동기화 자체가 없음 → 숨김. */}
              {posType !== 'wowpos' && (
              <div className="section">
                <button
                  className="sync-button"
                  onClick={() => {
                    // pos_type 을 그대로 넘긴다. 예전에는 okdc/metacity 가 아니면 전부
                    // 'unionpos' 로 뭉갰는데, 토스 매장이 유니온 동기화 API 를 부르게 됐다.
                    if (onSyncStart) onSyncStart(posType);
                    handleClose();
                  }}
                >
                  <svg
                    className="sync-button-icon"
                    width="15"
                    height="17"
                    viewBox="0 0 15 17"
                    fill="none"
                  >
                    <path d="M0 8.39596C0 6.28663 0.777 4.51971 2.331 3.09521C3.885 1.67071 5.733 0.958462 7.875 0.958462V0.352963C7.875 0.211796 7.93829 0.108546 8.06487 0.0432126C8.19146 -0.0221207 8.31687 -0.0133708 8.44112 0.0694626L10.3338 1.48696C10.5222 1.62813 10.6164 1.81655 10.6164 2.05221C10.6164 2.28788 10.5222 2.4763 10.3338 2.61746L8.44025 4.03496C8.31658 4.11779 8.19146 4.12655 8.06487 4.06121C7.93829 3.99588 7.875 3.89263 7.875 3.75146V3.14596C6.335 3.14596 5.00208 3.64705 3.87625 4.64921C2.75042 5.65138 2.1875 6.90029 2.1875 8.39596C2.1875 8.91104 2.25954 9.41067 2.40363 9.89484C2.54771 10.379 2.76063 10.8328 3.04238 11.2563C3.16896 11.4448 3.21767 11.646 3.1885 11.8601C3.15933 12.0742 3.05783 12.2442 2.884 12.3702L2.52787 12.631C2.31029 12.8019 2.07171 12.8681 1.81213 12.8296C1.55254 12.7911 1.34517 12.6616 1.19 12.4411C0.800333 11.8478 0.504583 11.2062 0.30275 10.5161C0.100917 9.82659 0 9.11988 0 8.39596ZM7 15.8335V16.439C7 16.5807 6.93671 16.684 6.81012 16.7487C6.68354 16.814 6.55812 16.8053 6.43387 16.7225L4.54212 15.305C4.35312 15.1638 4.25862 14.9754 4.25862 14.7397C4.25862 14.504 4.35283 14.3156 4.54125 14.1745L6.43475 12.757C6.55842 12.6741 6.68354 12.6654 6.81012 12.7307C6.93671 12.796 7 12.8993 7 13.0405V13.646C8.54 13.646 9.87292 13.1449 10.9987 12.1427C12.1246 11.1405 12.6875 9.89163 12.6875 8.39596C12.6875 7.88088 12.6155 7.37863 12.4714 6.88921C12.3273 6.39921 12.1144 5.94246 11.8326 5.51896C11.706 5.33054 11.6573 5.1293 11.6865 4.91521C11.7157 4.70113 11.8172 4.53109 11.991 4.40509L12.3471 4.14434C12.5647 3.97342 12.8033 3.9078 13.0629 3.94746C13.3225 3.98713 13.5301 4.11604 13.6859 4.33421C14.0715 4.93855 14.366 5.58313 14.5696 6.26796C14.7732 6.95279 14.875 7.66213 14.875 8.39596C14.875 10.5053 14.098 12.2722 12.544 13.6967C10.99 15.1212 9.142 15.8335 7 15.8335Z" fill="white"/>
                  </svg>
                  POS 동기화하기
                </button>
              </div>
              )}

              {/* 메뉴 표시 모드 (POS / 커스텀) — WowPos 매장은 wowpos_categories 가 이미
                  사장님이 만드는 커스텀 카테고리 역할이라 모드 구분 무의미. 토글 숨김.
                  ※ 버튼 3개(상품별출력·프린터·QR)만 헤더 없이 박스 배치, 나머지는 헤더 유지. */}
              {onMenuDisplayModeChange && posType !== 'wowpos' && (
                <div className="section">
                  <div className="section-header">
                    <span className="section-title">메뉴 표시 모드</span>
                    <span className="section-subtitle">주문페이지 메뉴 구성 방식을 선택하세요</span>
                  </div>
                  <div className="menu-mode-toggle">
                    <button
                      className={`menu-mode-btn ${menuDisplayMode === 'pos' ? 'active' : ''}`}
                      onClick={() => onMenuDisplayModeChange('pos')}
                      disabled={isModeSwitching}
                    >
                      POS 모드
                    </button>
                    <button
                      className={`menu-mode-btn ${menuDisplayMode === 'custom' ? 'active' : ''}`}
                      onClick={() => onMenuDisplayModeChange('custom')}
                      disabled={isModeSwitching}
                    >
                      커스텀 모드
                    </button>
                  </div>
                </div>
              )}

              {/* 버튼 3개(상품별출력·프린터매핑·QR연결)는 간격을 좁혀 하나로 묶는다 */}
              <div className="button-stack">

              {/* 상품별 출력 설정 — 자동포스팅/UI/프린터 3축을 메뉴별로 제어 (전 POS 공통) */}
              {['wowpos', 'metacity', 'union', 'unionpos', 'okdc', 'toss'].includes(posType) && (
              <div className="section">
                <button
                  className="table-qr-button"
                  onClick={() => {
                    setIsMenuOutputOpen(true);
                    isMenuOutputOpenRef.current = true;
                  }}
                >
                  <div className="table-qr-button-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" fill="#333"/>
                    </svg>
                    <div className="table-qr-button-text">
                      <span className="table-qr-button-title">상품별 출력 설정하기</span>
                      <span className="table-qr-button-desc">자동포스팅·UI·프린터를 메뉴별로 지정합니다</span>
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </button>
              </div>
              )}

              {/* 프린터 설정 버튼 — 매장 PC client 가 인식한 영수증 프린터 매핑.
                  전 POS 공통 노출(프린터 매핑은 receipt_yn 과 분리됨 — 전 메뉴 매핑 가능). */}
              {['wowpos', 'metacity', 'union', 'unionpos', 'okdc', 'toss'].includes(posType) && (
              <div className="section">
                <button
                  className="table-qr-button"
                  onClick={() => {
                    setIsPrinterManagerOpen(true);
                    isPrinterManagerOpenRef.current = true;
                  }}
                >
                  <div className="table-qr-button-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M19 8H5C3.34 8 2 9.34 2 11v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" fill="#333"/>
                    </svg>
                    <div className="table-qr-button-text">
                      <span className="table-qr-button-title">영수증 프린터 매핑하기</span>
                      <span className="table-qr-button-desc">메뉴별 출력 프린터를 지정합니다</span>
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </button>
              </div>
              )}

              {/* 테이블 QR 연결 버튼 */}
              <div className="section">
                <button
                  className="table-qr-button"
                  onClick={() => {
                    setIsTableQrManagerOpen(true);
                    isTableQrManagerOpenRef.current = true;
                    window.history.pushState({ tableQrManager: true }, '', window.location.href);
                  }}
                >
                  <div className="table-qr-button-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M3 11V3H11V11H3ZM5 5V9H9V5H5ZM3 21V13H11V21H3ZM5 15V19H9V15H5ZM13 11V3H21V11H13ZM15 5V9H19V5H15ZM13 21V19H15V21H13ZM13 13H15V15H13V13ZM15 15H17V17H15V15ZM13 17H15V19H13V17ZM17 13H19V15H17V13ZM19 15H21V17H19V15ZM17 17H19V19H17V17ZM19 19H21V21H19V19ZM17 19V21H19V19H17Z" fill="#333"/>
                    </svg>
                    <div className="table-qr-button-text">
                      <span className="table-qr-button-title">테이블 QR 연결 관리하기</span>
                      <span className="table-qr-button-desc">QR 코드와 테이블을 연결/해지합니다</span>
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </button>
              </div>

              <div className="section">
                <button
                  className="table-qr-button"
                  onClick={() => {
                    setIsSalesPageOpen(true);
                    isSalesPageOpenRef.current = true;
                  }}
                >
                  <div className="table-qr-button-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 20V4h2v14h14v2H4zm4-4V9h3v7H8zm5 0V5h3v11h-3zm5 0v-4h3v4h-3z" fill="#333"/>
                    </svg>
                    <div className="table-qr-button-text">
                      <span className="table-qr-button-title">매출 보기</span>
                      <span className="table-qr-button-desc">와우오더로 접수된 주문 매출을 확인합니다</span>
                    </div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#999">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </button>
              </div>
              </div>{/* /button-stack */}

              {/* 알림 설정 섹션 - 전 POS(wowpos 포함) 클라이언트 UI 알림음·알림창 설정 */}
              {(posType === 'okdc' || posType === 'union' || posType === 'unionpos' || posType === 'metacity' || posType === 'wowpos' || posType === 'toss') && (
                <div className="section">
                  <div className="section-header">
                    <span className="section-title">알림 설정</span>
                    <span className="section-subtitle">주문 알림 방식을 설정합니다</span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #E8E8E8', borderRadius: '12px', padding: '4px 16px' }}>
                    <div className="notification-toggle-row">
                      <div>
                        <div className="notification-toggle-label">주문 알림음</div>
                        <div className="notification-toggle-desc">주문 시 포스에서 알림음을 재생합니다</div>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notifySound}
                          onChange={(e) => handleNotificationToggle('sound', e.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                    <div className="notification-toggle-row">
                      <div>
                        <div className="notification-toggle-label">주문 알림창</div>
                        <div className="notification-toggle-desc">주문 시 포스에서 알림창을 표시합니다</div>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notifyPopup}
                          onChange={(e) => handleNotificationToggle('popup', e.target.checked)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* 영업시간 — 전 POS 공통 (판매시간 입력칸 재사용)
                  미입력 시 제한 없음. 입력하면 영업시간 외 주문페이지 진입이 차단된다. */}
              <div className="section">
                <div className="section-header">
                  <span className="section-title" style={{ whiteSpace: 'nowrap' }}>영업시간</span>
                  <span className="section-subtitle">영업시간을 입력해주세요!</span>
                </div>
                <BusinessHoursField
                  openTime={businessOpenTime}
                  closeTime={businessCloseTime}
                  onChange={onBusinessHoursChange}
                />
              </div>

              {/* 매장명 섹션 */}
              <div className="section">
                <div className="section-header">
                  <span className="section-title">매장 정보</span>
                  <span className="section-subtitle">손님에게 보이는 매장명이에요</span>
                </div>
                <div className={`input-container ${storeData.storeName ? 'filled' : ''} ${focusedField === 'storeName' ? 'focused' : ''} ${errors.storeName ? 'error' : ''}`}>
                  <div className={`input-label ${errors.storeName ? 'error' : ''}`}>매장명</div>
                  <textarea
                    ref={storeNameRef}
                    id="store-name"
                    className="input-field"
                    placeholder="매장명을 입력해주세요"
                    value={storeData.storeName}
                    onChange={(e) => handleInputChange('storeName', e.target.value)}
                    onFocus={() => setFocusedField('storeName')}
                    onBlur={() => setFocusedField(null)}
                    onKeyDown={(e) => handleKeyDown(e, businessNameRef)}
                    enterKeyHint="next"
                    rows={1}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                </div>
              </div>

              {/* 사업자 정보 섹션 */}
              <div className="section">
                <div className="section-header">
                  <span className="section-title">사업자 정보</span>
                  <span className="section-subtitle">POS 동기화 시 자동으로 채워집니다</span>
                </div>
                <div className="business-info-group">
                  <div className={`input-container ${storeData.businessName ? 'filled' : ''} ${focusedField === 'businessName' ? 'focused' : ''}`}>
                    <div className="input-label">상호명</div>
                    <textarea
                      ref={businessNameRef}
                      id="business-name"
                      className="input-field"
                      placeholder="POS 동기화 시 자동 입력"
                      value={storeData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      onFocus={() => setFocusedField('businessName')}
                      onBlur={() => setFocusedField(null)}
                      onKeyDown={(e) => handleKeyDown(e, addressRef)}
                      enterKeyHint="next"
                      rows={1}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                  <div className={`input-container ${storeData.address ? 'filled' : ''} ${focusedField === 'address' ? 'focused' : ''}`}>
                    <div className="input-label">주소</div>
                    <textarea
                      ref={addressRef}
                      id="address"
                      className="input-field"
                      placeholder="POS 동기화 시 자동 입력"
                      value={storeData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      onFocus={() => setFocusedField('address')}
                      onBlur={() => setFocusedField(null)}
                      onKeyDown={(e) => handleKeyDown(e, phoneNumberRef)}
                      enterKeyHint="next"
                      rows={1}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                  <div className={`input-container ${storeData.phoneNumber ? 'filled' : ''} ${focusedField === 'phoneNumber' ? 'focused' : ''}`}>
                    <div className="input-label">전화번호</div>
                    <textarea
                      ref={phoneNumberRef}
                      id="phone-number"
                      className="input-field"
                      placeholder="POS 동기화 시 자동 입력"
                      value={storeData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      onFocus={() => setFocusedField('phoneNumber')}
                      onBlur={() => setFocusedField(null)}
                      onKeyDown={(e) => handleKeyDown(e, businessNumberRef)}
                      enterKeyHint="next"
                      rows={1}
                    />
                  </div>
                  <div className={`input-container ${storeData.businessNumber ? 'filled' : ''} ${focusedField === 'businessNumber' ? 'focused' : ''}`}>
                    <div className="input-label">사업자등록번호</div>
                    <textarea
                      ref={businessNumberRef}
                      id="business-number"
                      className="input-field"
                      placeholder="POS 동기화 시 자동 입력"
                      value={storeData.businessNumber}
                      onChange={(e) => handleInputChange('businessNumber', e.target.value)}
                      onFocus={() => setFocusedField('businessNumber')}
                      onBlur={() => setFocusedField(null)}
                      onKeyDown={(e) => handleKeyDown(e, originsRef)}
                      enterKeyHint="next"
                      rows={1}
                    />
                  </div>
                </div>
              </div>

              {/* 원산지 섹션 */}
              <div className="section">
                <div className="section-header">
                  <span className="section-title">원산지 정보</span>
                  <span className="section-subtitle">손님에게 보이는 원산지 정보에요</span>
                </div>
                <div className={`input-container ${storeData.origins ? 'filled' : ''} ${focusedField === 'origins' ? 'focused' : ''}`}>
                  <div className="input-label">원산지 (선택)</div>
                  <textarea
                    ref={(el) => {
                      originsRef.current = el;
                      if (el) {
                        el.style.height = 'auto';
                        el.style.height = el.scrollHeight + 'px';
                      }
                    }}
                    id="origins"
                    className="input-field"
                    placeholder="예) 쌀 (국내산), 배추김치 (국내산)"
                    value={storeData.origins}
                    onChange={(e) => handleInputChange('origins', e.target.value)}
                    onFocus={() => setFocusedField('origins')}
                    onBlur={() => setFocusedField(null)}
                    rows={1}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                </div>
              </div>

              {/* 로그아웃 버튼 */}
              <button className="logout-button" onClick={() => {
                // 로그아웃 처리 - 양쪽 저장소에서 인증 정보 삭제
                localStorage.removeItem('currentUser');
                localStorage.removeItem('token');
                localStorage.removeItem('rememberMe');
                localStorage.removeItem('storeId');
                sessionStorage.removeItem('currentUser');
                sessionStorage.removeItem('token');
                window.location.href = '/login';
              }}>
                <svg
                  className="logout-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path d="M4.16667 4.16667H9.16667C9.625 4.16667 10 3.79167 10 3.33333C10 2.875 9.625 2.5 9.16667 2.5H4.16667C3.25 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.25 17.5 4.16667 17.5H9.16667C9.625 17.5 10 17.125 10 16.6667C10 16.2083 9.625 15.8333 9.16667 15.8333H4.16667V4.16667Z" fill="#ABABAB"/>
                  <path d="M17.2083 9.70837L14.8833 7.38337C14.8254 7.32383 14.751 7.28293 14.6696 7.26592C14.5883 7.24891 14.5037 7.25656 14.4268 7.28788C14.3498 7.3192 14.284 7.37277 14.2376 7.44174C14.1913 7.51071 14.1666 7.59194 14.1667 7.67503V9.1667H8.33333C7.875 9.1667 7.5 9.5417 7.5 10C7.5 10.4584 7.875 10.8334 8.33333 10.8334H14.1667V12.325C14.1667 12.7 14.6167 12.8834 14.875 12.6167L17.2 10.2917C17.3667 10.1334 17.3667 9.8667 17.2083 9.70837Z" fill="#ABABAB"/>
                </svg>
                <span>로그아웃</span>
              </button>
            </div>
            )}
          </div>

          {/* 저장 버튼 제거 — 자동 저장 (변경 후 0.8초 debounce + 닫을 때 강제 저장) */}
        </div>
      </div>

      {/* 테이블 QR 연결 관리 페이지 */}
      <TableQrManager
        isOpen={isTableQrManagerOpen}
        onClose={() => {
          setIsTableQrManagerOpen(false);
          isTableQrManagerOpenRef.current = false;
        }}
        onScannerOpenChange={(isOpen) => {
          isScannerOpenRef.current = isOpen;
        }}
        closeScannerRef={closeScannerRef}
      />

      {/* 영수증 프린터 매핑 페이지 */}
      <PrinterManager
        isOpen={isPrinterManagerOpen}
        posType={posType}
        onClose={() => {
          setIsPrinterManagerOpen(false);
          isPrinterManagerOpenRef.current = false;
        }}
      />

      {/* 상품별 출력 설정 페이지 */}
      <MenuOutputSettings
        isOpen={isMenuOutputOpen}
        posType={posType}
        onClose={() => {
          setIsMenuOutputOpen(false);
          isMenuOutputOpenRef.current = false;
        }}
      />

      {/* 매출 대시보드 페이지 (storeId 는 토큰에서 자체 확보) */}
      <SalesPage
        isOpen={isSalesPageOpen}
        posType={posType}
        onClose={() => {
          setIsSalesPageOpen(false);
          isSalesPageOpenRef.current = false;
        }}
      />

    </>
  );
};

export default StoreSettings;